import { useState, useRef, useEffect } from 'react';
import type {
  AnalysisOption,
  EventActivationMeta,
  EventAudience,
  EventSupport,
  ProductCategory,
} from '../../shared/types';
import {
  EVENT_AUDIENCE_OPTIONS,
  EVENT_SUPPORT_OPTIONS,
} from '../../shared/types';
import AnalysisIcon from './ui/AnalysisIcons';
import TypologyShape from './ui/TypologyShape';
import BackButton from './ui/BackButton';
import ProductCategorySelect from './ProductCategorySelect';
import { useAuth } from '../contexts/AuthContext';
import TrademarkAvailabilitySection from './TrademarkAvailabilitySection';

interface Props {
  option: AnalysisOption;
  error: string | null;
  onSubmit: (data: {
    textContent?: string;
    context?: string;
    images?: File[];
    productCategory?: ProductCategory;
    event?: EventActivationMeta;
  }) => void;
  onBack: () => void;
}

// Support-specific placeholders — prompt the user for the facts that
// actually flip the verdict (pourquoi, comment, combien, dans quel but).
function eventSupportPlaceholder(support: EventSupport): string {
  switch (support) {
    case 'goodies':
      return 'Ex: 500 piluliers de voyage marqués, distribués gratuitement aux visiteurs du stand. But : faire connaître la gamme auprès des pharmaciens présents.';
    case 'plv':
      return 'Ex: réglette de comptoir + stop-rayon A5 posés devant les boîtes dans le rayon automédication de l\'officine. Durée : 3 semaines.';
    case 'stand':
      return 'Ex: stand 12 m² au salon PharmagoraPlus, scénographie blanche, grand visuel packshot 2 m, table de démonstration.';
    case 'affichage_local':
      return 'Ex: 20 affichettes A3 disposées dans l\'espace de vente et au comptoir. Numéro de visa GP et mentions obligatoires en bas. But : orienter les patients vers le conseil du pharmacien.';
    case 'signaletique':
      return 'Ex: vitrophanie saisonnière sur la vitrine de l\'officine, visible depuis la rue. But : signaler l\'opération « préparez votre hiver ».';
    case 'echantillonnage':
      return 'Ex: démonstration d\'un autotensiomètre par une animatrice formée, 1 appareil de test. Aucune remise d\'échantillon de médicament. But : faire découvrir le dispositif.';
    case 'personnel':
      return 'Ex: 1 animatrice en blouse aux couleurs du laboratoire. Script : présenter la gamme, orienter vers le pharmacien. Aucune incitation à l\'achat de médicaments.';
    case 'comptoir':
      return 'Ex: chevalets de comptoir + leaflets d\'information avec renvoi aux mentions complètes. Durée : 2 mois.';
    case 'autre':
      return 'Décrivez librement le support, son rôle dans l\'opération, les quantités, le but recherché.';
    default:
      return '';
  }
}

/** Durée de l'animation de fermeture — alignée sur `.animate-app-out`. */
const CLOSE_MS = 220;

export default function ContentUploader({ option, error, onSubmit, onBack }: Props) {
  const { user, previewPlan } = useAuth();
  // Mirror the gating logic used by AnalysisTypeSelector: only show admin-only
  // tooling to super-admins NOT currently previewing another plan.
  const isSuperAdmin = user?.isSuperAdmin === true && previewPlan === null;
  const showTrademarkPanel = isSuperAdmin && option.key === 'brand_name';

  const [textContent, setTextContent] = useState('');
  const [context, setContext] = useState('');
  const [images, setImages] = useState<File[]>([]);
  // Étape zéro : qualification réglementaire du produit (défaut = audit prudentiel).
  const [productCategory, setProductCategory] = useState<ProductCategory>('undecided');
  const [eventAudience, setEventAudience] = useState<EventAudience>('undecided');
  const [eventSupports, setEventSupports] = useState<EventSupport[]>([]);
  const [eventSupportDetails, setEventSupportDetails] = useState<Partial<Record<EventSupport, string>>>({});
  const [eventName, setEventName] = useState('');
  const [eventLocation, setEventLocation] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [fileWarning, setFileWarning] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fermeture : l'écran se replie avant de rendre la main au sélecteur, en
  // miroir du « lancement » joué à l'ouverture.
  const [closing, setClosing] = useState(false);
  const closeTimer = useRef<number | null>(null);
  useEffect(() => () => { if (closeTimer.current) clearTimeout(closeTimer.current); }, []);

  const handleBack = () => {
    if (closing) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { onBack(); return; }
    setClosing(true);
    closeTimer.current = window.setTimeout(onBack, CLOSE_MS - 40);
  };

  const MAX_FILE_BYTES = 5 * 1024 * 1024;

  const isEvent = option.key === 'event_activation';
  // La catégorie produit s'applique à toutes les analyses portant sur un
  // produit — pas à la question réglementaire libre (ni à Visaly Créa, qui a
  // son propre formulaire).
  const showProductCategory = option.key !== 'general_question';
  const isText = option.inputType === 'text' || option.inputType === 'text+image';
  const isImage = option.inputType === 'image';
  const hasOptionalImages = option.inputType === 'text+image';
  // Event: accept either a description OR images (or both). Not strict on text.
  const canSubmit = isEvent
    ? textContent.trim().length > 0 || images.length > 0
    : isText ? textContent.trim().length > 0 : images.length > 0;

  const toggleSupport = (support: EventSupport) => {
    setEventSupports((prev) =>
      prev.includes(support) ? prev.filter((s) => s !== support) : [...prev, support],
    );
    // Clear the detail text when the user unchecks a support — avoids
    // sending stale details the user edited then deselected.
    setEventSupportDetails((prev) => {
      if (!prev[support]) return prev;
      const next = { ...prev };
      delete next[support];
      return next;
    });
  };

  const setSupportDetail = (support: EventSupport, value: string) => {
    setEventSupportDetails((prev) => ({ ...prev, [support]: value }));
  };

  const handleFiles = (files: FileList | null) => {
    if (!files) return;
    const arr = Array.from(files);
    const validType = arr.filter((f) =>
      ['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(f.type),
    );
    const accepted = validType.filter((f) => f.size <= MAX_FILE_BYTES);
    const rejectedType = arr.length - validType.length;
    const rejectedSize = validType.length - accepted.length;

    const warnings: string[] = [];
    if (rejectedType > 0) {
      warnings.push(`${rejectedType} fichier(s) ignoré(s) : format non supporté (JPG, PNG, WebP uniquement).`);
    }
    if (rejectedSize > 0) {
      warnings.push(`${rejectedSize} fichier(s) ignoré(s) : poids supérieur à 5 Mo.`);
    }
    setFileWarning(warnings.length ? warnings.join(' ') : null);
    setImages((prev) => [...prev, ...accepted]);
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    // Keep only details for still-checked supports, trim & drop empties.
    const cleanedDetails: Partial<Record<EventSupport, string>> = {};
    for (const s of eventSupports) {
      const val = eventSupportDetails[s]?.trim();
      if (val) cleanedDetails[s] = val;
    }
    const eventMeta: EventActivationMeta | undefined = isEvent
      ? {
          audience: eventAudience,
          supports: eventSupports,
          supportDetails: Object.keys(cleanedDetails).length ? cleanedDetails : undefined,
          eventName: eventName.trim() || undefined,
          eventLocation: eventLocation.trim() || undefined,
          eventDate: eventDate.trim() || undefined,
          operationBrief: textContent.trim() || undefined,
        }
      : undefined;
    onSubmit({
      textContent: isText || isEvent ? textContent : undefined,
      context: context.trim() || undefined,
      images: images.length > 0 ? images : undefined,
      productCategory: showProductCategory ? productCategory : undefined,
      event: eventMeta,
    });
  };

  return (
    <div className={`mx-auto max-w-2xl ${closing ? 'animate-app-out' : 'animate-app-in'}`}>
      {/* Retour + bandeau d'en-tête : l'aplat de la typologie, posé sur le
          crème — c'est lui qui prend le relais du clone d'ouverture. */}
      <BackButton onClick={handleBack} label="Retour" />
      <div className="hero-tint mb-6 flex items-center gap-4 rounded-3xl px-5 py-5 sm:px-6">
        <span className="hero-pill grid h-12 w-12 shrink-0 place-items-center">
          <TypologyShape type={option.key} color="currentColor" />
          <AnalysisIcon type={option.key} className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <h2 className="font-display text-[1.6rem] leading-tight text-ink sm:text-2xl">
            {option.label}
          </h2>
          <p className="text-sm text-ink/65">{option.description}</p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Étape zéro — qualification réglementaire du produit */}
      {showProductCategory && (
        <ProductCategorySelect value={productCategory} onChange={setProductCategory} />
      )}

      {/* Text Input */}
      {(isText || isEvent) && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            {option.key === 'brand_name'
              ? 'Nom de marque souhaité'
              : option.key === 'slogan'
              ? 'Slogan ou accroche'
              : option.key === 'general_question'
              ? 'Votre question'
              : isEvent
              ? <>Brief de l'opération <span className="text-slate-400 font-normal">(optionnel si vous uploadez des visuels)</span></>
              : 'Décrivez votre concept'}
          </label>
          <div className="glow-input">
            <textarea
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              placeholder={
                option.key === 'brand_name'
                  ? 'Ex: "Sereniflor" • "Dolréal" • "Laboratoires Alvea"'
                  : option.key === 'slogan'
                  ? 'Ex: "Soulage dès la première prise" • "Vos défenses naturelles, tout l\'hiver" • "La douceur d\'une formule d\'origine végétale" • "Le réflexe des hivers sereins"'
                  : option.key === 'general_question'
                  ? 'Ex: "Ai-je le droit de mentionner le prix d\'un médicament OTC dans une publicité grand public ?"'
                  : isEvent
                  ? 'Ex: "Lancement de la gamme hiver en officine : vitrophanie, réglette de comptoir, présentoir, animation par une conseillère le samedi, leaflets d\'information au comptoir."'
                  : 'Décrivez votre concept créatif en détail...'
              }
              rows={option.key === 'concept' || option.key === 'general_question' || isEvent ? 6 : 3}
              className="w-full rounded-xl border border-slate-300 bg-cream-app px-4 py-3 text-slate-900 placeholder:text-slate-400 outline-none transition-shadow resize-y min-h-[3rem]"
              autoFocus
            />
          </div>
        </div>
      )}

      {/* Trademark availability — admin-only pre-screen for brand_name */}
      {showTrademarkPanel && (
        <TrademarkAvailabilitySection initialBrandName={textContent} />
      )}

      {/* Image Upload (required for image types, optional for text+image) */}
      {(isImage || hasOptionalImages) && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            {option.key === 'label'
              ? 'Maquette(s) du support PLV'
              : option.key === 'concept'
              ? <>Images d'inspiration <span className="text-slate-400 font-normal">(optionnel)</span></>
              : isEvent
              ? <>Visuels de l'opération <span className="text-slate-400 font-normal">(goodies, stand, PLV, affichettes — optionnel)</span></>
              : 'Visuels de campagne'}
          </label>

          {/* Drop Zone */}
          <div
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('is-over'); }}
            onDragLeave={(e) => { e.currentTarget.classList.remove('is-over'); }}
            onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove('is-over'); handleFiles(e.dataTransfer.files); }}
            className="drop-tint cursor-pointer rounded-3xl p-8 text-center"
          >
            <svg className="w-10 h-10 mx-auto mb-3 text-ink/40" fill="none" viewBox="0 0 24 24" strokeWidth={1.6} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
            <p className="text-sm text-ink/75">
              Cliquez ou glissez-d&eacute;posez vos images ici
            </p>
            <p className="text-xs text-ink/45 mt-1">JPG, PNG, WebP — 5 Mo max par fichier</p>
            {hasOptionalImages && (
              <p className="text-xs text-ink/50 mt-2">Maquettes, prototypes, moodboards... pour enrichir l'analyse visuelle de votre concept</p>
            )}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={(e) => handleFiles(e.target.files)}
              className="hidden"
            />
          </div>

          {/* File warning (size / type rejected client-side) */}
          {fileWarning && (
            <p className="mt-2 text-xs text-amber-600">
              {fileWarning}
            </p>
          )}

          {/* Preview */}
          {images.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {images.map((img, i) => (
                <div key={i} className="relative group">
                  <div className="w-20 h-20 rounded-xl bg-slate-100 border border-slate-200 overflow-hidden">
                    {img.type.startsWith('image/') ? (
                      <img
                        src={URL.createObjectURL(img)}
                        alt={img.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full text-xs text-slate-500">PDF</div>
                    )}
                  </div>
                  <button
                    onClick={() => removeImage(i)}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Event Activation — nom/lieu/date → audience → supports (avec détail par support) */}
      {isEvent && (
        <>
          <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="glow-input">
              <label className="block text-xs font-medium text-slate-500 mb-1">Nom de l'opération</label>
              <input
                type="text"
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
                placeholder="Ex: Lancement gamme hiver 2026"
                className="w-full rounded-xl border border-slate-300 bg-cream-app px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 outline-none"
              />
            </div>
            <div className="glow-input">
              <label className="block text-xs font-medium text-slate-500 mb-1">Lieu</label>
              <input
                type="text"
                value={eventLocation}
                onChange={(e) => setEventLocation(e.target.value)}
                placeholder="Ex: Officine, PharmagoraPlus, congrès"
                className="w-full rounded-xl border border-slate-300 bg-cream-app px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 outline-none"
              />
            </div>
            <div className="glow-input">
              <label className="block text-xs font-medium text-slate-500 mb-1">Date</label>
              <input
                type="text"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                placeholder="Ex: 15 mai 2026"
                className="w-full rounded-xl border border-slate-300 bg-cream-app px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 outline-none"
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Audience de l'opération
            </label>
            <p className="text-xs text-slate-500 mb-3">
              Le régime applicable diffère selon que l'opération vise les professionnels de santé (visa PM, encadrement anti-cadeaux) ou le grand public (visa GP, primes et cadeaux interdits — R5122-4 14°).
            </p>
            <div className="space-y-2">
              {EVENT_AUDIENCE_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={`opt-tint flex items-start gap-3 p-3 rounded-xl cursor-pointer ${
                    eventAudience === opt.value ? 'is-on' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="eventAudience"
                    value={opt.value}
                    checked={eventAudience === opt.value}
                    onChange={() => setEventAudience(opt.value)}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-900">
                      {opt.label}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {opt.description}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Supports présents dans l'opération
            </label>
            <p className="text-xs text-slate-500 mb-3">
              Cochez chaque support concerné, puis précisez pourquoi, comment, combien, dans quel but. Plus vos explications sont précises, plus le verdict des juges sera affiné.
            </p>
            <div className="space-y-2">
              {EVENT_SUPPORT_OPTIONS.map((opt) => {
                const checked = eventSupports.includes(opt.value);
                return (
                  <div
                    key={opt.value}
                    className={`opt-tint rounded-xl ${checked ? 'is-on' : ''}`}
                  >
                    <label className="flex items-start gap-3 p-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSupport(opt.value)}
                        className="mt-0.5"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-slate-900">
                          {opt.label}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {opt.description}
                        </div>
                      </div>
                    </label>
                    {checked && (
                      <div className="px-3 pb-3 pl-10 animate-fade-in">
                        <textarea
                          value={eventSupportDetails[opt.value] ?? ''}
                          onChange={(e) => setSupportDetail(opt.value, e.target.value)}
                          placeholder={eventSupportPlaceholder(opt.value)}
                          rows={3}
                          className="w-full rounded-xl border border-slate-300 bg-cream-app px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-shadow resize-y min-h-[3rem]"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Context (optional, always shown) */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Contexte additionnel <span className="text-slate-400 font-normal">(optionnel)</span>
        </label>
        <div className="glow-input">
          <textarea
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder="Ex: complément alimentaire à base de plantes, cible adultes 30-55 ans, vente en officine et parapharmacie..."
            rows={2}
            className="w-full rounded-xl border border-slate-300 bg-cream-app px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-shadow resize-none"
          />
        </div>
      </div>

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={!canSubmit}
        className="font-display w-full rounded-full bg-ink py-3.5 text-white transition-transform hover:scale-[1.01] active:scale-100 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:scale-100"
      >
        {option.key === 'general_question' ? 'Poser ma question' : 'Lancer l\'analyse de conformit\u00E9'}
      </button>
    </div>
  );
}
